import { z } from 'zod';
import { FreshCardCheckoutStateSchema, FreshCardPaymentPresentationSchema, } from './fresh-card';
import { AllPaymentProvidersEnum } from '../types/common-tools/providers';
import { PositiveMoneyCentsSchema } from '../types/sale/v4/execution';
import { BillingIntervalSchema } from '../types/subscriptions/enums';
import { CheckoutModeEnum, CheckoutSourceKindEnum, PublicCheckoutStatusEnum, PublicCheckoutStatusSchema, PublicExperienceProviderSchema, PublicExperienceSchemaVersionSchema, PublicExperienceSchemaVersionV2Schema, } from './enums';
import { ProviderExecutionScopeSchema } from './execution-scope';
import { AcceptedBillingScheduleSchema, VerifiedBillingSetupSchema, BillingScheduleOperationSchema, } from './billing-schedule';
import { AcceptedOfferSnapshotSchema, PublicExperienceCurrencySchema, } from './offer';
import { BusinessIdSchema, CheckoutCapabilitySchema, CheckoutIdSchema, ExperienceIdSchema, HttpsUrlSchema, IsoTimestampSchema, ProviderAccountDbIdSchema, ProviderIdempotencyKeySchema, PublicExperiencePublicIdSchema, PublishedRevisionSchema, Sha256HexSchema, StripeAccountIdSchema, StripeCheckoutClientSecretSchema, StripeCheckoutSessionIdSchema, StripeCustomerIdSchema, StripeInvoiceIdSchema, StripePaymentIntentIdSchema, StripePriceIdSchema, StripePublishableKeySchema, StripeSubscriptionIdSchema, SubmissionIdSchema, } from './primitives';
/**
 * Public Experiences — the checkout boundary.
 *
 * Contains the local state machine, the private correlation document, the guest
 * capability transport and the deliberately narrow browser payment-presentation
 * DTO. No raw provider object is ever part of the application contract.
 */
/**
 * Forward-only local state machine. Applied inside a Firestore transaction; any
 * transition not listed here is rejected, which makes duplicate and
 * out-of-order webhook deliveries harmless.
 *
 *   INITIALIZING    -> PENDING_PAYMENT | FAILED | EXPIRED
 *   PENDING_PAYMENT -> PROCESSING | CONFIRMED | EXPIRED | FAILED
 *   PROCESSING      -> CONFIRMED | EXPIRED | FAILED
 *   CONFIRMED / EXPIRED / FAILED -> (terminal)
 *
 * `PENDING_PAYMENT -> CONFIRMED` is allowed on purpose: `invoice.paid` can arrive
 * before `checkout.session.completed`, and payment evidence outranks session
 * bookkeeping.
 */
export const PUBLIC_CHECKOUT_STATUS_TRANSITIONS = {
    [PublicCheckoutStatusEnum.INITIALIZING]: [
        PublicCheckoutStatusEnum.PENDING_PAYMENT,
        PublicCheckoutStatusEnum.EXPIRED,
        PublicCheckoutStatusEnum.FAILED,
    ],
    [PublicCheckoutStatusEnum.PENDING_PAYMENT]: [
        PublicCheckoutStatusEnum.BILLING_SCHEDULED,
        PublicCheckoutStatusEnum.PROCESSING,
        PublicCheckoutStatusEnum.CONFIRMED,
        PublicCheckoutStatusEnum.EXPIRED,
        PublicCheckoutStatusEnum.FAILED,
    ],
    [PublicCheckoutStatusEnum.PROCESSING]: [
        PublicCheckoutStatusEnum.BILLING_SCHEDULED,
        PublicCheckoutStatusEnum.CONFIRMED,
        PublicCheckoutStatusEnum.EXPIRED,
        PublicCheckoutStatusEnum.FAILED,
    ],
    [PublicCheckoutStatusEnum.CONFIRMED]: [],
    [PublicCheckoutStatusEnum.BILLING_SCHEDULED]: [
        PublicCheckoutStatusEnum.CONFIRMED,
    ],
    [PublicCheckoutStatusEnum.EXPIRED]: [],
    [PublicCheckoutStatusEnum.FAILED]: [],
};
/** Terminal states never transition again, not even to themselves. */
export const TERMINAL_PUBLIC_CHECKOUT_STATUSES = [
    PublicCheckoutStatusEnum.BILLING_SCHEDULED,
    PublicCheckoutStatusEnum.CONFIRMED,
    PublicCheckoutStatusEnum.EXPIRED,
    PublicCheckoutStatusEnum.FAILED,
];
export const isTerminalPublicCheckoutStatus = (status) => TERMINAL_PUBLIC_CHECKOUT_STATUSES.includes(status);
/**
 * True when `from -> to` is a permitted transition. A no-op re-delivery
 * (`from === to`) returns `false`: the caller must treat it as "already applied"
 * and skip, not as an error.
 */
export const canTransition = (from, to) => PUBLIC_CHECKOUT_STATUS_TRANSITIONS[from].includes(to);
/**
 * Provider state recorded on the checkout document. Every field is optional
 * because the document is created **before** the provider call so a crash
 * between the two is recoverable.
 */
export const CheckoutProviderStateSchema = z
    .object({
    freshCard: FreshCardCheckoutStateSchema.optional(),
    billingSetup: VerifiedBillingSetupSchema.optional(),
    billingScheduleOperation: BillingScheduleOperationSchema.optional(),
    applicationFeePercent: z.number().min(0).max(100).optional(),
    preparedReturnUrl: HttpsUrlSchema.optional(),
    provider: PublicExperienceProviderSchema,
    /** Account snapshot, so relinking never breaks lookup or reconciliation. */
    accountId: StripeAccountIdSchema,
    priceId: StripePriceIdSchema,
    idempotencyKey: ProviderIdempotencyKeySchema,
    sessionId: StripeCheckoutSessionIdSchema.optional(),
    /**
     * Session-scoped. Issued only to the authorized guest context and MUST be
     * covered by the backend redaction lists — never logged (BE-08).
     */
    clientSecret: StripeCheckoutClientSecretSchema.optional(),
    sessionExpiresAt: IsoTimestampSchema.optional(),
    customerId: StripeCustomerIdSchema.optional(),
    subscriptionId: StripeSubscriptionIdSchema.optional(),
    firstInvoiceId: StripeInvoiceIdSchema.optional(),
    firstPaymentIntentId: StripePaymentIntentIdSchema.optional(),
})
    .strict();
/** Non-fatal billing problem observed on a later invoice. Never a local status. */
export const CheckoutBillingIssueSchema = z
    .object({
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(500),
    invoiceId: StripeInvoiceIdSchema.optional(),
    occurredAt: IsoTimestampSchema,
})
    .strict();
/** Recorded failure reason. Safe for admin display; never contains PII. */
export const CheckoutErrorSchema = z
    .object({
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(500),
    occurredAt: IsoTimestampSchema,
})
    .strict();
/**
 * Private checkout/correlation document
 * (`/publicExperiencePrivate/{businessId}/experiences/{experienceId}/
 * pxCheckouts/{checkoutId}`). Server-only; no client read path.
 *
 * RETENTION: this is a short-lived checkout/security artifact, not a business
 * record. It holds the capability digest, the session client secret and provider
 * session ids. The TARGET retention is 90 days (enough for operator recovery and
 * dispute lookups), but **automatic expiration is NOT implemented in MVP1**:
 * there is no TTL policy, no cleanup job and no deletion path, so documents
 * persist until a future retention policy is built. Customer form submissions
 * (`pxSubmissions`) are a separate record class with no expiration at all — see
 * `PublicExperienceSubmissionDocumentSchema`.
 *
 * `requestHash` binds a retry to the same payload: replaying with the same hash
 * returns the same provider session plus a freshly minted capability, while a
 * different hash for the same `checkoutId` is `PXE-004 ATTEMPT_CONFLICT`.
 * Only the capability's sha256 digest is stored — never the secret.
 */
const CheckoutDocumentBaseShape = {
    checkoutId: CheckoutIdSchema,
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    publicId: PublicExperiencePublicIdSchema,
    submissionId: SubmissionIdSchema,
    status: PublicCheckoutStatusSchema,
    mode: z.literal(CheckoutModeEnum.RECURRING),
    sourceKind: z.literal(CheckoutSourceKindEnum.PUBLIC_EXPERIENCE_FORM),
    requestHash: Sha256HexSchema,
    /**
     * sha256 of the capability secret. The raw capability is NEVER stored and
     * NEVER logged — it must be on the backend redaction lists (BE-08) — and is
     * compared with a timing-safe comparison.
     */
    capabilityHash: Sha256HexSchema,
    capabilityExpiresAt: IsoTimestampSchema,
    /**
     * The canonical offer terms AS USED AT CREATION. Stored on the checkout
     * itself — together with `provider.priceId` — so reconciling or auditing this
     * attempt never depends on the experience's current configuration: the offer
     * can be republished, re-priced or disabled afterwards without rewriting
     * history.
     */
    offerRevision: PublishedRevisionSchema,
    acceptedAmountCents: PositiveMoneyCentsSchema,
    currency: PublicExperienceCurrencySchema,
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52),
    provider: CheckoutProviderStateSchema,
    /** Last provider event applied, for idempotent webhook processing. */
    lastEventId: z.string().min(1).max(255).optional(),
    lastBillingIssue: CheckoutBillingIssueSchema.optional(),
    error: CheckoutErrorSchema.optional(),
    confirmedAt: IsoTimestampSchema.optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
};
/**
 * Contract 1.0.0 checkout document (platform-owned, destination charge),
 * retained verbatim. A v1 checkout is never rewritten and is never replayed
 * after the direct-charge cutover (`PXE-016 OFFER_REPUBLISH_REQUIRED`).
 */
export const PublicCheckoutDocumentV1Schema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionSchema,
    ...CheckoutDocumentBaseShape,
})
    .strict();
/**
 * Contract 2.0.0 checkout document: `schemaVersion: 2` plus the REQUIRED
 * `executionScope`, frozen inside the claim transaction BEFORE the first
 * provider write. `provider.accountId` keeps its v1 meaning (account snapshot);
 * v2 readers route by `executionScope` only. A v2 document without a scope is
 * a parse failure → `PXE-018 SCOPE_UNRESOLVED`, never a platform fallback.
 */
export const PublicCheckoutDocumentSchema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionV2Schema,
    billingSchedule: AcceptedBillingScheduleSchema.optional(),
    ...CheckoutDocumentBaseShape,
    executionScope: ProviderExecutionScopeSchema,
})
    .strict();
/** Discriminated on `schemaVersion` — the only legacy discriminator. */
export const AnyPublicCheckoutDocumentSchema = z.discriminatedUnion('schemaVersion', [PublicCheckoutDocumentV1Schema, PublicCheckoutDocumentSchema]);
/**
 * The ENTIRE payment presentation handed to the browser under contract 1.0.0
 * (retained for documentation and legacy tests; no route serves it once the
 * direct-charge cutover flag is on).
 *
 * Three fields plus an expiry: a publishable key (not a secret), a
 * session-scoped client secret, and when the session stops being usable. No
 * Stripe SDK object, no account id, no amounts, no customer or subscription id.
 */
export const ProviderPaymentPresentationV1Schema = z
    .object({
    provider: z.literal(AllPaymentProvidersEnum.STRIPE),
    publishableKey: StripePublishableKeySchema,
    clientSecret: StripeCheckoutClientSecretSchema,
    sessionExpiresAt: IsoTimestampSchema,
})
    .strict();
/**
 * Contract 2.0.0 payment presentation — exactly FIVE fields.
 *
 * `connectedAccountId` is a server-chosen SDK routing identifier: the browser
 * initialises Stripe.js with `{ stripeAccount: connectedAccountId }` so the
 * Embedded Checkout session created in the connected account can be mounted.
 * It is not a secret and it is not accepted from a client: the create request
 * still forbids `accountId`/`stripeAccountId`, and the value comes solely from
 * the published linkage. Still no SDK object, no amounts, no customer or
 * subscription id, and never a secret (`sk_`/`rk_`) key.
 */
export const ProviderPaymentPresentationSchema = z
    .object({
    provider: z.literal(AllPaymentProvidersEnum.STRIPE),
    publishableKey: StripePublishableKeySchema,
    connectedAccountId: StripeAccountIdSchema,
    clientSecret: StripeCheckoutClientSecretSchema,
    sessionExpiresAt: IsoTimestampSchema,
})
    .strict();
export const RecurringPaymentPresentationSchema = z.union([
    ProviderPaymentPresentationSchema,
    FreshCardPaymentPresentationSchema,
]);
/**
 * The guest capability as returned to the browser: the secret plus its expiry.
 * Held in `sessionStorage` only, sent back as `X-Checkout-Capability`.
 */
export const CheckoutCapabilityGrantSchema = z
    .object({
    capability: CheckoutCapabilitySchema,
    capabilityExpiresAt: IsoTimestampSchema,
})
    .strict();
/** Capability lifetime (PLAN D6). Independent of the 60 min session expiry. */
export const CHECKOUT_CAPABILITY_TTL_SECONDS = 86400;
/** Server-prepared correlation; never accepted from a public HTTP body. */
export const RecurringCheckoutSourceSchema = z
    .object({
    kind: z.literal(CheckoutSourceKindEnum.PUBLIC_EXPERIENCE_FORM),
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    submissionId: SubmissionIdSchema,
    checkoutId: CheckoutIdSchema,
})
    .strict();
export const PaymentLinkCheckoutSourceSchema = z
    .object({
    kind: z.literal(CheckoutSourceKindEnum.PAYMENT_LINK),
    businessId: BusinessIdSchema,
    requestId: z.string().uuid(),
    requestRevision: PublishedRevisionSchema,
    checkoutId: CheckoutIdSchema,
    saleId: z.string().uuid(),
    /** The reserved All_Payment_Transactions row, not a provider id. */
    paymentId: z.string().uuid(),
})
    .strict();
export const CheckoutSourceSchema = z.discriminatedUnion('kind', [
    RecurringCheckoutSourceSchema,
    PaymentLinkCheckoutSourceSchema,
]);
/**
 * Merchant routing, fully server-derived. Never accepted from a guest request.
 *
 * Part 2: `executionScope` is REQUIRED — every provider call the instruction
 * leads to takes the scope and builds `{ stripeAccount, idempotencyKey }` from
 * it. `accountId` / `providerAccountDbId` keep their v1 meaning and must agree
 * with the scope's merchant snapshot (a mismatch is a parse failure, so a stale
 * relink can never be smuggled into a direct instruction).
 */
export const CheckoutMerchantSchema = z
    .object({
    provider: PublicExperienceProviderSchema,
    providerAccountDbId: ProviderAccountDbIdSchema,
    accountId: StripeAccountIdSchema,
    applicationFeePercent: z.number().min(0).max(100),
    executionScope: ProviderExecutionScopeSchema,
})
    .strict()
    .superRefine((merchant, ctx) => {
    if (merchant.accountId !== merchant.executionScope.merchantAccountId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['executionScope', 'merchantAccountId'],
            message: 'executionScope.merchantAccountId must equal accountId',
        });
    }
    if (merchant.providerAccountDbId !==
        merchant.executionScope.providerAccountDbId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['executionScope', 'providerAccountDbId'],
            message: 'executionScope.providerAccountDbId must equal providerAccountDbId',
        });
    }
});
/**
 * Fixed one-time payment terms. Money is integer USD cents; no catalog Price,
 * recurrence or invoice is needed. The backend freezes these before dispatch.
 */
export const OneTimeCheckoutPaymentSchema = z
    .object({
    amountMinor: z.number().int().positive().safe().max(99999999),
    currency: z.literal('USD'),
    description: z.string().trim().min(1).max(500),
})
    .strict();
export const OneTimeCheckoutMerchantSchema = z
    .object({
    provider: PublicExperienceProviderSchema,
    providerAccountDbId: ProviderAccountDbIdSchema,
    accountId: StripeAccountIdSchema,
    /** Calculated once from the frozen onlineProcessingConfig, in cents. */
    applicationFeeAmountMinor: z.number().int().nonnegative().safe(),
    executionScope: ProviderExecutionScopeSchema,
})
    .strict()
    .superRefine((merchant, ctx) => {
    const scope = merchant.executionScope;
    if (scope.chargeModel !== 'DIRECT' ||
        scope.merchantAccountId !== merchant.accountId ||
        scope.providerAccountDbId !== merchant.providerAccountDbId)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['executionScope'],
            message: 'One-time payments require the original matching DIRECT scope',
        });
});
/** Internal, server-derived instructions. Recurring fields stay unchanged. */
export const RecurringCheckoutInstructionSchema = z
    .object({
    source: RecurringCheckoutSourceSchema,
    mode: z.literal(CheckoutModeEnum.RECURRING),
    offerSnapshot: AcceptedOfferSnapshotSchema,
    merchant: CheckoutMerchantSchema,
    customer: z
        .object({
        email: z.string().email().max(160),
        name: z.string().min(1).max(201),
    })
        .strict(),
    returnUrl: HttpsUrlSchema,
    idempotencyKey: ProviderIdempotencyKeySchema,
})
    .strict();
const oneTimeInstruction = z
    .object({
    source: PaymentLinkCheckoutSourceSchema,
    mode: z.literal(CheckoutModeEnum.ONE_TIME),
    payment: OneTimeCheckoutPaymentSchema,
    merchant: OneTimeCheckoutMerchantSchema,
    /** Anonymous link possession never grants access to a recipient's cards. */
    customer: z.object({ policy: z.literal('CREATE_NEW') }).strict(),
    returnUrl: HttpsUrlSchema,
    idempotencyKey: ProviderIdempotencyKeySchema,
    expiresAt: IsoTimestampSchema,
})
    .strict();
const validateOneTimeFee = (instruction, ctx) => {
    if (instruction.merchant.applicationFeeAmountMinor >
        instruction.payment.amountMinor)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['merchant', 'applicationFeeAmountMinor'],
            message: 'Application fee cannot exceed the requested amount',
        });
};
export const OneTimeCheckoutInstructionSchema = oneTimeInstruction.superRefine(validateOneTimeFee);
/** Mode narrowing exposes only the fields that that billing shape supports. */
export const CheckoutInstructionSchema = z
    .discriminatedUnion('mode', [
    RecurringCheckoutInstructionSchema,
    oneTimeInstruction,
])
    .superRefine((instruction, ctx) => {
    if (instruction.mode === CheckoutModeEnum.ONE_TIME)
        validateOneTimeFee(instruction, ctx);
});
/** What the provider adapter returns to `CheckoutService`. */
export const CheckoutSessionResultSchema = z
    .object({
    sessionId: StripeCheckoutSessionIdSchema,
    clientSecret: StripeCheckoutClientSecretSchema,
    expiresAt: IsoTimestampSchema,
})
    .strict();
